<template>
    <div class="relative inline-block text-left">
        <template v-if="squareButton">
            <button
                @click="openForm()"
                type="button"
                class="text-indigo-700 bg-indigo-100 hover:bg-indigo-200 inline-flex items-center px-1.5 py-1.5 border border-transparent text-xs font-medium rounded focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
                <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                    <path
                        fill-rule="evenodd"
                        d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
                        clip-rule="evenodd"
                    ></path>
                </svg>
            </button>
        </template>
        <AppButton element="button" @click="openForm()" type="button" v-else>
            {{ $t('messages.transfer_between_types') }}
        </AppButton>

        <div>
            <SlideOver :title="$t('messages.transfer_between_types')" :show="open" @close="open = false">
                <form :action="url" method="post" @submit.prevent="submit">
                    <div class="grid grid-cols-2 gap-2 md:gap-6">
                        <div class="col-span-2">
                            <div class="mt-1">
                                <FormLabel :value="$t('messages.from')" />
                                {{ student.full_name }}
                            </div>
                        </div>

                        <div class="col-span-2">
                            <div class="mt-1">
                                <FormLabel :value="$t('messages.to')" for="to_student_id" />
                                <FormSelect
                                    required
                                    id="to_student_id"
                                    v-model="form.to_student_id"
                                    :options="recipients"
                                    class="mt-1 w-full"
                                    :null-option="false"
                                />
                                <FormError :value="form.errors.to_student_id" class="mt-2" />
                            </div>
                        </div>

                        <div class="col-span-2">
                            <div class="mt-1">
                                <FormLabel :value="$t('messages.date')" for="paid_at">
                                    <span v-if="!form.paid_at">
                                        <button @click="setDateToToday()" class="text-xs text-indigo-400" type="button">
                                            {{ $t('messages.today?') }}
                                        </button>
                                    </span>
                                </FormLabel>
                                <FormInput
                                    required
                                    id="paid_at"
                                    v-model="form.paid_at"
                                    name="paid_at"
                                    type="date"
                                    class="mt-1 w-full"
                                />
                                <FormError :value="form.errors.paid_at" class="mt-2" />
                            </div>
                        </div>

                        <div class="">
                            <div class="mt-1">
                                <FormLabel :value="`${$t('messages.from')} (${$t('messages.type')})`" for="select_id" />
                                <FormSelect
                                    required
                                    id="from_select_id"
                                    v-model="form.from_select_id"
                                    :options="selects"
                                    class="mt-1 w-full"
                                />
                                <FormError :value="form.errors.from_select_id" class="mt-2" />
                            </div>
                            <div class="mt-1" :class="form.amount ? 'visible' : 'invisible'">-{{ form.amount }}</div>
                        </div>

                        <div class="">
                            <div class="mt-1">
                                <FormLabel :value="`${$t('messages.to')} (${$t('messages.type')})`" for="select_id" />
                                <FormSelect
                                    required
                                    id="to_select_id"
                                    v-model="form.to_select_id"
                                    :options="selects"
                                    class="mt-1 w-full"
                                />
                                <FormError :value="form.errors.to_select_id" class="mt-2" />
                                <div class="mt-1" :class="form.amount ? 'visible' : 'invisible'">
                                    {{ form.amount }}
                                </div>
                            </div>
                        </div>

                        <div class="col-span-2">
                            <div class="mt-1">
                                <FormLabel :value="$t('messages.quantity')" for="amount" />
                                <FormInput
                                    required
                                    id="amount"
                                    v-model="form.amount"
                                    name="amount"
                                    type="number"
                                    class="mt-1 w-full"
                                    step="0.01"
                                    min="0.01"
                                />
                                <FormError :value="form.errors.amount" class="mt-2" />
                            </div>
                        </div>

                        <div class="col-span-1"></div>

                        <div class="col-span-1">
                            <div class="mt-1">
                                <FormLabel :value="$tc('messages.scholarships', 1)" for="scholarship_id" />
                                <FormSelect
                                    id="scholarship_id"
                                    v-model="form.scholarship_id"
                                    :options="scholarships"
                                    class="mt-1 w-full"
                                />
                                <FormError :value="form.errors.scholarship_id" class="mt-2" />
                            </div>
                        </div>

                        <div class="col-span-2">
                            <div class="mt-1">
                                <FormLabel :value="$t('messages.comment')" for="comment">
                                    <template v-slot:corner>
                                        <CharCounter
                                            :value="form.comment"
                                            :max="180"
                                            show-max
                                            class="text-sm font-medium leading-5 text-gray-500"
                                        />
                                    </template>
                                </FormLabel>
                                <FormTextarea
                                    id="comment"
                                    v-model="form.comment"
                                    name="comment"
                                    maxlength="180"
                                    class="mt-1 w-full"
                                />
                                <FormError :value="form.errors.comment" class="mt-2" />
                            </div>
                        </div>
                    </div>

                    <div class="flex justify-end mt-5">
                        <AppButton desired-action element="button" type="submit" :disabled="form.processing">
                            {{ $t('messages.save') }}
                            <span v-if="form.processing" class="ml-1 btn-spinner"></span>
                        </AppButton>
                    </div>
                </form>
            </SlideOver>
        </div>
    </div>
</template>

<script>
import FormInput from '@/components/Forms/FormInput.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import FormError from '@/components/Forms/FormError.vue';
import FormSelect from '@/components/Forms/FormSelect.vue';
import AppButton from '@/components/AppButton.vue';
import SlideOver from '@/components/SlideOver.vue';
import FormTextarea from '@/components/Forms/FormTextarea.vue';
import CharCounter from '@/components/Forms/CharCounter.vue';

export default {
    emits: ['close'],
    name: 'StudentPaymentTransferFormPanel',

    components: {
        FormInput,
        FormLabel,
        FormError,
        FormSelect,
        FormTextarea,
        CharCounter,
        AppButton,
        SlideOver,
    },

    props: {
        squareButton: {
            required: false,
            type: Boolean,
            default: false,
        },
        url: {
            required: true,
            type: String,
        },
        student: {
            required: true,
            type: Object,
        },
        selects: {
            required: true,
            type: Array,
        },
        scholarships: {
            required: true,
            type: Array,
        },
        recipients: {
            required: true,
            type: Array,
        },
    },

    data() {
        return {
            open: false,
            form: this.$inertia.form({
                student_id: this.student.id,
                to_student_id: this.student.id,
                amount: null,
                paid_at: new Date().toISOString().slice(0, 10),
                scholarship_id: null,
                from_select_id: null,
                to_select_id: null,
                comment: this.$t('messages.transfer_between_types'),
            }),
        };
    },

    methods: {
        dismiss() {
            this.$emit('close');
        },
        openForm() {
            this.open = true;
        },
        setDateToToday() {
            this.form.paid_at = new Date().toISOString().slice(0, 10);
        },
        submit() {
            const url = this.url;
            this.form.post(url, {
                preserveScroll: true,
                onSuccess: () => {
                    this.form.reset('amount', 'scholarship_id', 'from_select_id', 'to_select_id', 'to_student_id');
                    this.open = false;
                },
            });
        },
    },
};
</script>
